import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Panel } from '@/components/ui/Panel';
import { num } from '@/lib/format';
import { portalDemand } from '@/mocks/portal';

export default function PortalDemandPage() {
  const { t } = useTranslation();
  const chartRows = portalDemand.slice(0, 8);
  const max = Math.max(...chartRows.map((r) => r.requested));
  const totalRequested = portalDemand.reduce((s, r) => s + r.requested, 0);

  return (
    <div>
      <div className="mb-5 border-b border-background-200 pb-4">
        <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.demand.title')}</h1>
        <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.demand.subtitle')}</p>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel padded={false} className="xl:col-span-2">
          <div className="flex flex-col gap-1 border-b border-background-200 px-5 py-3.5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground-950">{t('portal.demand.chartTitle')}</h2>
              <p className="mt-0.5 text-xs text-foreground-500">{t('portal.demand.chartSub')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium text-foreground-600">
                <span className="h-2 w-2 rounded-sm bg-primary-500" />
                {t('portal.demand.chartLegend')}
              </span>
              <span className="rounded-full border border-background-200 px-2 py-0.5 text-[10px] text-foreground-500">
                {t('portal.demand.last90')}
              </span>
            </div>
          </div>

          <div className="px-4 pb-4 pt-5">
            <div className="flex gap-3">
              <div className="flex h-[210px] flex-col justify-between pb-6 text-right text-[9.5px] text-foreground-400">
                <span>{max}</span>
                <span>{Math.round(max * 0.66)}</span>
                <span>{Math.round(max * 0.33)}</span>
                <span>0</span>
              </div>
              <div className="relative min-w-0 flex-1">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-[186px]">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className="absolute inset-x-0 border-t border-dashed border-background-200"
                      style={{ top: `${(i / 3) * 100}%` }}
                    />
                  ))}
                </div>
                <div className="relative flex h-[210px] items-end gap-1.5 overflow-x-auto pb-6 thin-scroll sm:gap-3">
                  {chartRows.map((r) => (
                    <div key={r.model} className="flex h-full min-w-[38px] flex-1 flex-col items-center justify-end">
                      <span className="tnum mb-1 text-[9.5px] font-semibold text-foreground-600">{r.requested}</span>
                      <div
                        className="w-full max-w-[46px] rounded-t bg-primary-500 transition-all hover:bg-primary-600"
                        style={{ height: `${Math.max(4, (r.requested / max) * 176)}px` }}
                        title={`${r.model} · ${r.requested} ${t('portal.demand.unitsLabel')}`}
                      />
                      <span className="tnum mt-1.5 w-full truncate text-center text-[9px] font-medium text-foreground-500">
                        {r.model}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <Panel className="flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-accent-100 text-accent-700">
                <i className="ri-lightbulb-line text-[13px]" />
              </span>
              <h2 className="text-sm font-semibold text-foreground-950">{t('portal.demand.hintTitle')}</h2>
            </div>
            <p className="mt-2 text-[12px] leading-5 text-foreground-600">{t('portal.demand.hintBody')}</p>
          </div>
          <div className="mt-4">
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div className="rounded-md border border-background-200 bg-background-100 px-2.5 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-foreground-500">{t('portal.demand.colAdded')}</p>
                <p className="tnum mt-0.5 text-[15px] font-semibold text-foreground-900">18</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 px-2.5 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-foreground-500">
                  {t('portal.demand.colMissing')}
                </p>
                <p className="tnum mt-0.5 text-[15px] font-semibold text-rose-600">24</p>
              </div>
            </div>
            <Link
              to="/portal/products"
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-archive-2-line text-[13px]" />
              {t('portal.demand.hintCta')}
            </Link>
          </div>
        </Panel>
      </div>

      <Panel padded={false}>
        <div className="flex items-start justify-between gap-3 border-b border-background-200 px-4 py-3">
          <div>
            <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.demand.title')}</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">
              {t('portal.demand.footer', { count: 87 })}
            </p>
          </div>
          <span className="tnum hidden rounded-full border border-background-200 px-2 py-0.5 text-[10.5px] text-foreground-500 sm:inline">
            {num(totalRequested)} {t('portal.demand.unitsLabel')}
          </span>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[760px] border-collapse text-[12px]">
            <thead>
              <tr className="bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.demand.colModel')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.demand.colAdded')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.demand.colReserved')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.demand.colOrdered')}</th>
                <th className="px-4 py-2.5 text-right font-medium">{t('portal.demand.colMissing')}</th>
              </tr>
            </thead>
            <tbody>
              {portalDemand.map((r) => (
                <tr key={r.model} className="border-t border-background-200/70 hover:bg-background-100/50">
                  <td className="px-4 py-2.5">
                    <span className="tnum text-[11.5px] font-semibold text-primary-700">{r.model}</span>
                  </td>
                  <td className="tnum px-3 py-2.5 text-right text-foreground-700">
                    {t('portal.demand.times', { count: r.timesAdded })}
                  </td>
                  <td className="tnum px-3 py-2.5 text-right text-foreground-700">{r.reserved}</td>
                  <td className="tnum px-3 py-2.5 text-right text-foreground-700">{r.ordered}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.missing > 0 ? (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-rose-700">
                        {r.missing} · {t('portal.demand.missingHint')}
                      </span>
                    ) : (
                      <span className="text-[11px] text-foreground-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-background-200 px-4 py-2.5 text-[10.5px] text-foreground-400">
          {t('portal.reservations.specNote')}
        </p>
      </Panel>
    </div>
  );
}